import React, { useState } from "react";
import { Row, Col, Container, Form } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faCancel, faCheck, faDollar } from '@fortawesome/free-solid-svg-icons';

import DiscountReasonModal from "../../Modals/DiscountReasonModal";
import { useCouponReasonTypes } from "../../Store/entities/couponReasonTypes/hooks";
import TextDivider from "../../Parts/TextDivider";
import Button from "../../Parts/Button";
import {
	UseMultipliersByQuoteId,
	useQuoteById,
	useQuotePricing,
	useQuoteTypeNameByQuoteId
} from "../../Store/entities/quotes/hooks";
import apiCalls from "../../PowerShadesAPIFunctions";
import { useAssembliesByQuoteId } from "../../Store/entities/assemblies/hooks";
import { fakeRootType } from "../../FakeRootType";
import { getQuoteColor } from "../QuoteObjectExtensions";
import getEffectivePricingMultiplier from "../pricingContext";
import PaymentMethodButton from "./PaymentMethodButton";
import { useDealerById, useDistributorById, useDistributorLocationById } from "../../Store/entities/hooks";
import OrderModalHeader from "./OrderModalHeader";

type AdminPaymentDetailsProps = {
	quoteId: number;
	ROOT: fakeRootType;
	close: () => void;
	back: () => void;
};

const AdminPaymentDetails = ({ quoteId, ROOT, close, back }: AdminPaymentDetailsProps) => {
	const quote = useQuoteById(quoteId);
	const quoteAssemblies = useAssembliesByQuoteId(quoteId);

	const [processing, setProcessing] = useState(false);
	const [success, setSuccess] = useState(false);
	const [showDiscountModal, setShowDiscountModal] = useState(false);
	const couponReasonTypes = useCouponReasonTypes();

	const quoteType = useQuoteTypeNameByQuoteId(quoteId);
	
	const isRma = quoteType.toLocaleLowerCase() === "rma";
	const quoteMult = UseMultipliersByQuoteId(quoteId);
	const effectivePricingMultiplier = getEffectivePricingMultiplier({
		dealerId: quote?.dealer_id,
		distributorId: quote?.distributor_id,
		multipliers: quoteMult,
	});
	const currentMultiplierName = quoteMult?.currentMultiplier;
	const currentMultiplier = quoteMult?.list.find((m) => m.name === currentMultiplierName);
	const dealerMultiplier = quoteMult?.list.find((m) => m.name.includes("Dealer")) ?? null;
	const defaultMultiplier = quote.quote_type_id === 6
		? 0.3
		: dealerMultiplier?.multiplier ?? currentMultiplier?.multiplier ?? 1;
	const distributorMultiplier = quoteMult?.list.find((m) => m.name.includes("Distributor")) ?? null;

	const defaultQuotePrice = useQuotePricing(quoteId, defaultMultiplier);
	const effectiveQuotePrice = useQuotePricing(
		quoteId,
		effectivePricingMultiplier.multiplier,
		effectivePricingMultiplier.currency_multiplier
	);
	const distributorMultiplierQuotePrice = useQuotePricing(quoteId, distributorMultiplier?.multiplier ?? 1);
	const currentMultiplierQuotePrice = useQuotePricing(quoteId, dealerMultiplier?.multiplier ?? 1);
	const couponTotal = effectiveQuotePrice?.coupon_total ?? 0.0;
	const couponName = quote.coupon_name ?? "";

	const shippingPrice = defaultQuotePrice?.total_shipping_price ?? 0.0;
	const taxPercentage = defaultQuotePrice?.tax_rate ?? 0.0;
	const salePrice = defaultQuotePrice?.total_price ?? 0.0;
	const taxShipping = defaultQuotePrice?.is_shipping_taxable ?? false;
	const rawTaxPct = ((defaultQuotePrice?.tax_rate ?? 0) * 100).toFixed(4).replace(/\.?0+$/, '');
	const [taxOverrideString, setTaxOverrideString] = useState(rawTaxPct);

	const [currentAction, setCurrentAction] = useState<
	"dealer_placer" | "zero_out" | "price_override">("dealer_placer");

	const dealer = useDealerById(quote.dealer_id ?? 0);
	const isCCDealer = dealer?.allow_credit_card_payments ?? false;

	const toFixedNumber = (num, digits = 2) => {
		const pow = 10 ** digits;
		const numLarge = num * pow;
		const numRounded = Math.round(numLarge);
		const finalNum = numRounded / pow;
		return finalNum;
	};

	const formatUsd = (value: number | string | null | undefined) => Number(value ?? 0).toFixed(2);

	const defaultShippingPrice = (defaultQuotePrice?.parcel_shipping_price ?? 0)
		+ (defaultQuotePrice?.awning_shipping_price ?? 0);

	const defaultObj = {
		quote_id: quoteId,
		tax_percentage: defaultQuotePrice?.tax_rate ?? 0,
		tax_percentage_string: `${defaultQuotePrice?.tax_rate ?? 0}`,
		sale_price: defaultQuotePrice?.subtotal ?? 0,
		sale_price_string: defaultQuotePrice?.subtotal
			.toFixed(2)
			.toString() ?? "0.0",
		// Awning freight is tracked under freight_shipping_price, but has always been part of
		// this default alongside parcel shipping, so it stays included here.
		shipping_price: defaultShippingPrice,
		shipping_price_string: `${defaultShippingPrice}`,
		tax_shipping: defaultQuotePrice?.is_shipping_taxable ?? false,
		installation_cost: null,
		installation_cost_string: "0.0",
		installation_single_string: "0.0",
		misc_cost: null,
		misc_cost_string: "0.0",
		final_price: 0.0,
		final_price_string: "0.0",
		credit_card: isCCDealer ? 0.0 : null,
		credit_card_string: "0.0",
		multiplier: defaultMultiplier !== 0 ? defaultMultiplier : 0.5,
		multiplier_string:
			defaultMultiplier !== 0 ? defaultMultiplier.toString() : "0.5",
		coupon_name: quote?.coupon_name ?? "",
		coupon_snapshot: quote?.coupon_snapshot ?? 0,
		coupon_snapshot_string: toFixedNumber(quote?.coupon_snapshot ?? 0, 2).toString(),
	};

	// Sectioning off this legacy fixed number code with an ifee
	(() => {
		const numPrototype = Number.prototype as any;
		numPrototype.toFixedNumber = (digits, base) => {
			const pow = (base || 10) ** digits;
			return Math.round((this ?? 0) * pow) / pow;
		};
	})();

	const totalPrice = toFixedNumber(currentMultiplierQuotePrice?.total_price ?? 0);
	const finalPrice = (
		currentMultiplierQuotePrice?.subtotal ?? 0.0
	) - (
		currentMultiplierQuotePrice?.coupon_total ?? 0.0
	);
	const [finalPriceString, setFinalPrice] = useState(`${(
		currentMultiplierQuotePrice?.subtotal ?? 0.0
	) - (
		currentMultiplierQuotePrice?.coupon_total ?? 0.0
	)}`);

	const distributor = useDistributorById(quote.distributor_id || 0);
	const hasDistributor = distributor?.id !== 0;

	const isRMACediaAndHasNoPreviousQuoteId = (quoteType === "rework" || quoteType === "rma")
		&& quoteAssemblies.length > 0
		&& !((quote?.previous_quote_id ?? 0) > 0);

	const submitAdminPayment = (reason: string, couponReasonTypeId: number | null) => {
		const data = { ...defaultObj };

		data.sale_price = defaultObj?.sale_price ?? totalPrice;
		data.final_price = defaultObj?.final_price ?? defaultObj?.sale_price ?? totalPrice;
		data.tax_percentage = (Number(taxOverrideString) || 0) / 100;

		const priceToSave = currentAction === "zero_out" ? 0
			: currentAction === "price_override"
				? Number(finalPriceString)
				: (distributor?.sales_type === 'direct') && (dealer?.allow_credit_card_payments ?? false)
					? (
						distributorMultiplierQuotePrice?.total_price ?? 0
					) - (
						distributorMultiplierQuotePrice?.coupon_total ?? 0
					)
					: finalPrice;

		const {
			sale_price: SalePrice,
			shipping_price: ShippingPrice,
			tax_shipping: TaxShipping,
			tax_percentage: TaxPercentage,
			sale_price: TotalPrice,
			multiplier: Multiplier,
		} = data;

		apiCalls.submitOrderPressed(quoteId);

		apiCalls.submitAdminPayment(
			quoteId,
			SalePrice ?? 0,
			ShippingPrice ?? 0,
			(TaxShipping ?? false ? 1 : 0),
			TaxPercentage ?? 0,
			0,
			0,
			TotalPrice,
			priceToSave,
			Multiplier,
			true,
			reason,
			couponReasonTypeId ?? 0,
			data.coupon_name,
			+data.coupon_snapshot
		).then((resp) => {
			if (isRMACediaAndHasNoPreviousQuoteId) {
				ROOT.alert({
					text: 'Please enter the previous quote id (The PS#) in Advanced Settings to place this RMA/Rework',
					title: 'No Previous Quote Id',
					quickFormat: 'error',
				})
					.then(() => undefined)
					.catch(() => undefined);
			} else if (resp?.data?.success === true) {
				setSuccess(true);
				ROOT.alert({
					title: "Thanks for your order!",
					icon: "confetti",
					smallIcon: false,
					text: "Your order has been placed successfully!",
					noCancelButton: true,
					acceptButtonText: "Okay"
				}).then(() => window.location.reload());
			} else {
				ROOT.alert({
					title: "Order Process Failed",
					text: resp?.data?.errorMessage || resp?.data?.error_message || "Something Went Wrong.",
					quickFormat: "error"
				});
			}
		});

		setProcessing(true);
	};

	const handleDiscountModalCancel = () => {
		setShowDiscountModal(false);
	};

	const handleSubmitClick = () => {
		if (currentAction !== "dealer_placer") {
			setShowDiscountModal(true);
		} else {
			submitAdminPayment("None Recorded", null);
		}
	};

	const handleDiscountModalSubmit = async (_reason: string, _couponReasonTypeId: number | null) => {
		setShowDiscountModal(false);
		submitAdminPayment(_reason, _couponReasonTypeId);
	};

	const setDealerPlacer = () => {
		setCurrentAction("dealer_placer");
	};

	const setZeroOut = () => {
		setCurrentAction("zero_out");
	};

	const setPriceOverride = () => {
		setCurrentAction("price_override");
	};

	const priceBeforeCreditCardFee = currentAction === "zero_out"
		? 0
		: currentAction === "price_override" ? Number(finalPriceString) : finalPrice ?? 0;
	const creditCardAmount = (dealer?.allow_credit_card_payments ?? false)
		? toFixedNumber(priceBeforeCreditCardFee * 0.03, 2)
		: 0;
	const dealerMult = dealerMultiplier?.multiplier ?? 0.5;
	const distMult = distributorMultiplier?.multiplier ?? 0.425;
	const finalDistributorPrice = currentAction === "price_override"
		? toFixedNumber(
			((Number(finalPriceString) - shippingPrice) * (distMult / dealerMult)) + shippingPrice,
			2
		)
		: toFixedNumber(distributorMultiplierQuotePrice?.subtotal ?? 0, 2);
	const finalDealerPrice = toFixedNumber(priceBeforeCreditCardFee + creditCardAmount, 2);

	return !success ? (
		<Container className="local-bootstrap" style={{ overflow: "hidden auto" }}>
			<OrderModalHeader
				title="Admin Payment View"
				quoteType={quoteType}
				onClose={close}
			/>
			<div>
				<Row>
					<Col>
						<PaymentMethodButton
							quoteColor="PSGreen"
							key="psaAdmin"
							icon={faCheck}
							text="Place As Dealer"
							style={{ margin: '1em' }}
							onClick={setDealerPlacer}
							disabled={currentAction === "dealer_placer"}
						/>
					</Col>
					<Col>
						<PaymentMethodButton
							quoteColor="PSGreen"
							key="psaAdmin"
							icon={faCancel}
							text="Zero Out Order"
							style={{ margin: '1em' }}
							onClick={setZeroOut}
							disabled={currentAction === "zero_out"}
						/>
					</Col>
					<Col>
						<PaymentMethodButton
							quoteColor="PSGreen"
							key="price_override"
							icon={faDollar}
							text="Override Price"
							style={{ margin: '1em' }}
							onClick={setPriceOverride}
							disabled={currentAction === "price_override"}
						/>
					</Col>
				</Row>
			</div>
			<TextDivider>
				<h3 className={`text-${quoteType}`} style={{ fontWeight: "normal" }}>{`PS${quote.id}`}</h3>
			</TextDivider>
			<div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
				<div style={{ flex: "1 1 45%" }} className="d-flex flex">
					<Form.Label className="w-100">
						Sales Price (USD)
						<i
							title="Base sales price modified by the multiplier - does not include shipping or taxes"
							className="fas fa-info-circle ms-2"
						/>
						<Form.Control
							type="text"
							size="sm"
							value={formatUsd(salePrice)}
							disabled
						/>
					</Form.Label>
				</div>

				<div style={{ flex: "1 1 45%" }} className="d-flex flex">
					<Form.Label className="w-100">
						Shipping (USD)
						<div className="d-flex align-items-center gap-2">
							<Form.Control
								type="text"
								size="sm"
								disabled
								value={formatUsd(shippingPrice)}
							/>
						</div>
					</Form.Label>
				</div>

				<div style={{ flex: "1 1 45%" }} className="d-flex flex">
					<Form.Label className="w-100">
						Taxes
						<div className="d-flex align-items-center gap-2">
							<Form.Control
								type="text"
								size="sm"
								value={taxOverrideString}
								onChange={(e) => setTaxOverrideString(e.target.value)}
								onFocus={(e) => e.target.select()}
								style={{ maxWidth: "80px" }}
							/>
							<span>%</span>
						</div>
					</Form.Label>
					<div className="mt-2">
						<small className="text-muted">
							Tax (USD): $
							{formatUsd((Number(taxOverrideString) || 0) / 100 * salePrice)}
						</small>
					</div>
				</div>

				<div style={{ flex: "1 1 45%" }} className="d-flex flex">
					<Form.Label className="w-100">
						Shipping Tax (USD)
						<div className="d-flex align-items-center gap-2">
							<Form.Control
								type="text"
								size="sm"
								disabled={!taxShipping}
								value={
									taxShipping
										? formatUsd(shippingPrice)
										: "0.00"
								}
							/>
						</div>
					</Form.Label>
				</div>

				<div style={{ flex: "1 1 45%" }} className="d-flex flex">

					<Form.Label className="w-100">
						Coupon Name
						<div className="d-flex align-items-center gap-2">

							<Form.Control
								type="text"
								size="sm"
								disabled
								value={couponName}
							/>
						</div>
					</Form.Label>
				</div>

				<div style={{ flex: "1 1 45%" }} className="d-flex flex">
					<Form.Label className="w-100">
						Coupon Amount (USD)
						<Form.Control
							type="text"
							size="sm"
							disabled
							value={formatUsd(couponTotal)}
						/>
					</Form.Label>
				</div>

				<div style={{ flex: "1 1 45%" }} className="d-flex flex">
					<Form.Label className="w-100">
						{currentAction === "dealer_placer"
							? "Expected Final Price (USD)"
							: "Overridden Final Price (USD)"}
						<i
							title="Only use this if you absolutely have to -
							please explain any extra costs in the notes"
							className="fas fa-info-circle ms-2"
						/>
						<div className="d-flex align-items-center gap-2">
							<Form.Control
								type="text"
								size="sm"
								disabled={currentAction !== "price_override"}
								onChange={(e) => setFinalPrice(e.target.value)}
								value={currentAction === "dealer_placer"
									? formatUsd(priceBeforeCreditCardFee)
									: currentAction === "zero_out"
										? "0.00"
										: finalPriceString}
							/>
						</div>
					</Form.Label>
				</div>
				<div style={{ flex: "1 1 45%" }} className="d-flex flex">
					<Form.Label className="w-100">
						Credit Card 3% (USD)
						<i
							title="If there are any additional chargers we need to add to the invoice, enter them here"
							className="fas fa-info-circle ms-2"
						/>
						<div className="d-flex align-items-center gap-2">
							<Form.Control
								type="text"
								size="sm"
								disabled
								value={formatUsd(creditCardAmount)}
							/>
						</div>
					</Form.Label>
				</div>
				{currentAction === "price_override" ? <Row>
					<div style={{ flex: "1 1 45%" }} className="d-flex flex">
						<Form.Label className="w-100">
							Final Dealer Price (USD)
							<i
								title="Only use this if you absolutely have to -
								please explain any extra costs in the notes"
								className="fas fa-info-circle ms-2"
							/>
							<div className="d-flex align-items-center gap-2">
								<Form.Control
									type="text"
									size="sm"
									disabled
									value={formatUsd(finalDealerPrice)}
								/>
							</div>
						</Form.Label>
					</div>
					{hasDistributor && <div style={{ flex: "1 1 45%" }} className="d-flex flex">
						<Form.Label className="w-100">
							Final Dist Price (USD)
							<i
								title="Only use this if you absolutely have to -
								please explain any extra costs in the notes"
								className="fas fa-info-circle ms-2"
							/>
							<div className="d-flex align-items-center gap-2">
								<Form.Control
									type="text"
									size="sm"
									disabled
									value={formatUsd(finalDistributorPrice)}
								/>
							</div>
						</Form.Label>
					</div>}
				</Row> : <Row>
					<TextDivider>
						<h3
							className={`text-${quoteType}`}
							style={{ fontWeight: "normal" }}
						>
							{`Dealer Invoice: $${formatUsd(finalDealerPrice)}`}
						</h3>
					</TextDivider>
				</Row>}
			</div>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					marginTop: '0.75rem'
				}}
			>
				<button
					type="button"
					onClick={back}
					style={{
						background: 'none',
						marginRight: 'auto',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: getQuoteColor(quoteType),
						border: 'none',
						cursor: 'pointer',
						padding: 0,
					}}
				>
					<FontAwesomeIcon icon={faArrowLeftLong} />
					<span
						style={{
							fontSize: '20px',
							fontWeight: 'normal',
							textAlign: 'left',
							paddingLeft: '5px',
						}}
					>
						Back
					</span>
				</button>

				<button
					type="button"
					disabled={processing}
					onClick={handleSubmitClick}
					style={{
						background: 'none',
						marginLeft: 'auto',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: getQuoteColor(quoteType),
						border: 'none',
						cursor: processing ? 'not-allowed' : 'pointer',
						padding: 0,
						opacity: processing ? 0.6 : 1,
					}}
				>
					{
						processing
							? <span
								style={{
									fontSize: '20px',
									fontWeight: 'normal',
									textAlign: 'right',
								}}
							>
								Processing...
							</span>
							: <Button color={quoteType}>Submit</Button>
					}

				</button>
			</div>

			<DiscountReasonModal
				defaultReason={isRma ? "RMA" : (quote?.discount_reason ?? '')}
				open={showDiscountModal}
				onCancel={handleDiscountModalCancel}
				onSubmit={handleDiscountModalSubmit}
				quoteType={quoteType}
				couponReasonTypes={couponReasonTypes}
			/>
		</Container>
	) : null;
};

export default AdminPaymentDetails;
